import React, { useState, useMemo, useCallback } from 'react';

import { useGetEvent } from '@vlabs/api/artemy/client';
import type { EventId } from '@vlabs/api/artemy/model/EventId';
import { proxyArtemyUrl } from '@vlabs/api/axios/point2-client';
import { ZoomInIcon, DownloadIcon } from '@vlabs/icons';
import { useImage } from '@vlabs/shared/hooks';
import { ButtonScrimGroup, ButtonScrimDivider } from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';

import { useFullscreenImageViewer } from '@vlabs/monokit/features/view-photo';
import { downloadImage } from '@vlabs/monokit/shared/lib/images';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import type { DetectionColor } from '@vlabs/monokit/shared/ui/color-picker';
import { DetectionOverlaySettings } from '@vlabs/monokit/shared/ui/detection-overlay';
import type { DetectionOverlaySettingsValue, Detection } from '@vlabs/monokit/shared/ui/detection-overlay';
import { FullFrameWithDetections } from '@vlabs/monokit/shared/ui/full-frame-with-detections';

import st from './FullFrameDetectionsWithSettings.module.sass';
import { useFullFrameDetectionsWithSettingsTranslation } from './i18n';

const isImageOrigin = (imageOrigin?: string | null): imageOrigin is string => (
  !!imageOrigin && /^\/(1|6)\/images\//u.test(imageOrigin)
);

const getImageUrl = (imageOrigin?: string | null): string | undefined => {
  if (!isImageOrigin(imageOrigin)) return undefined;

  return proxyArtemyUrl(imageOrigin.replace(/^\/6\/images\//u, '/1/images/'));
};

const displayName = 'FullFrameDetectionsWithSettingsPoint3';
const testid = buildTestid(displayName);

interface FullFrameDetectionsWithSettingsPoint3Props extends WithClassNameProp, WithDataTestid {
  eventId: EventId;
}

function FullFrameDetectionsWithSettingsPoint3({
  eventId,
  className,
  'data-testid': dataTestId = undefined,
}: FullFrameDetectionsWithSettingsPoint3Props): React.JSX.Element {
  const { t } = useFullFrameDetectionsWithSettingsTranslation();

  const [color, setColor] = useState<DetectionColor>('positive');
  const [isDetectionVisible, setIsDetectionVisible] = useState<boolean>(true);

  const { data: event } = useGetEvent(
    eventId ?? '',
    { target: ['face_detections', 'body_detections'] },
    { query: { enabled: !!eventId } },
  );

  const detectionWithFullFrame = event?.face_detections?.find(({ image_origin }) => isImageOrigin(image_origin))
        ?? event?.body_detections?.find(({ image_origin }) => isImageOrigin(image_origin));
  const fullFrameUrl = getImageUrl(detectionWithFullFrame?.image_origin);
  const fullFrame = useImage(fullFrameUrl);
  const fullscreenImageViewer = useFullscreenImageViewer({ url: fullFrame.url ?? undefined });

  const faceDetection = event?.face_detections?.find(({ sample_id }) => !!sample_id);
  const bodyDetection = event?.body_detections?.find(({ sample_id }) => !!sample_id);

  const uiDetections = useMemo(() => {
    const detections = [];

    if (bodyDetection?.detection?.rect) {
      detections.push({
        ...bodyDetection.detection.rect,
        type: 'body',
      });
    }

    if (faceDetection?.detection?.rect) {
      detections.push({
        ...faceDetection.detection.rect,
        type: 'face',
      });
    }

    return detections;
  }, [bodyDetection, faceDetection]);

  const handleChangeSettings = useCallback((nextSettings: DetectionOverlaySettingsValue) => {
    setIsDetectionVisible(nextSettings.withDetections);
    if (nextSettings.color) setColor(nextSettings.color);
  }, []);

  return (
    <div
      className={cn(st.Root, className)}
      {...testid(dataTestId)}
    >
      <div className={st.FullFrame}>
        <FullFrameWithDetections
          className={st.FullFrame}
          detections={{
            type: 'bbox',
            coordinates: uiDetections,
            color,
          } as Detection}
          src={fullFrame.url ?? undefined}
          withDetections={isDetectionVisible}
        />

        {!!fullFrame.url && (
          <ButtonScrimGroup
            className={st.Actions}
            size="small"
          >
            <IconButton
              disabled={!fullFrame.url}
              icon={<ZoomInIcon />}
              kind="secondary"
              onClick={fullscreenImageViewer}
              size="small"
              variant="flat"
              {...testid('ZoomButton', dataTestId)}
            />

            <ButtonScrimDivider />

            <IconButton
              disabled={!fullFrame.url}
              icon={<DownloadIcon />}
              kind="secondary"
              onClick={() => downloadImage(fullFrame.url)}
              size="small"
              variant="flat"
              {...testid('DownloadButton', dataTestId)}
            />
          </ButtonScrimGroup>
        )}
      </div>

      {!!fullFrame.url && (
        <DetectionOverlaySettings
          className={st.Settings}
          label={t('settings.face_and_body')}
          onChangeSettings={handleChangeSettings}
          settings={{
            withDetections: isDetectionVisible,
            withColor: true,
            color,
          }}
        />
      )}
    </div>
  );
}

FullFrameDetectionsWithSettingsPoint3.displayName = displayName;

export { FullFrameDetectionsWithSettingsPoint3 };
